import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { executeOrder } from '../services/trading';
import socket from '../services/socket';
import MiniChart from './MiniChart';
import { useProMode } from '../context/ProModeContext';
import { subscribeToMarketData } from '../services/market';
import FlickerText from './FlickerText';
import { showToast } from './ToastNotification';
import { API_BASE_URL } from '../config';
import '../styles/Market.css';

const Market = ({ userId, marketState: propMarketState }) => {
  const navigate = useNavigate();
  const isProMode = true; // Pro mode is always enabled
  // Using showToast instead of old notification system
  const componentMounted = useRef(true);
  const [stocks, setStocks] = useState([]);
  const [sortOrder, setSortOrder] = useState('default');
  const [searchTerm, setSearchTerm] = useState('');
  const [userBalance, setUserBalance] = useState(10000);
  const [loading, setLoading] = useState(true);
  const [orderAmount, setOrderAmount] = useState('1');
  const [socketConnected, setSocketConnected] = useState(false);
  const [positions, setPositions] = useState([]);
  const [error, setError] = useState(null);
  const [localMarketState, setLocalMarketState] = useState(null);

  // Prefer local state (from HTTP polling) over prop state, since prop may be stale for logged-out users
  const marketState = localMarketState || propMarketState;

  // Fetch market state on mount and listen for updates
  useEffect(() => {
    const fetchMarketState = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/market/status`);
        if (response.ok) {
          const data = await response.json();
          if (data.marketState) {
            setLocalMarketState(data.marketState);
          }
        }
      } catch (err) {
        console.error('Error fetching market state:', err);
      }
    };

    // Fetch on mount + poll every 5s to keep banner accurate
    fetchMarketState();
    const pollInterval = setInterval(fetchMarketState, 5000);

    // Also re-fetch when tab becomes visible (user returns to tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchMarketState();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Listen for market state updates via socket
    const handleMarketBell = (data) => {
      setLocalMarketState(prev => ({
        ...prev,
        isOpen: data.type === 'OPENING_BELL',
        tradingEnabled: data.type === 'OPENING_BELL'
      }));
    };

    const handleTradingPaused = () => {
      setLocalMarketState(prev => ({ ...prev, tradingEnabled: false }));
    };

    const handleTradingResumed = () => {
      setLocalMarketState(prev => ({ ...prev, tradingEnabled: true }));
    };

    const handlePartyStateUpdate = (data) => {
      if (data.marketState) {
        setLocalMarketState(prev => ({
          ...prev,
          isOpen: data.marketState.isOpen,
          tradingEnabled: data.marketState.tradingEnabled
        }));
      }
    };

    socket.on('market-bell', handleMarketBell);
    socket.on('trading-paused', handleTradingPaused);
    socket.on('trading-resumed', handleTradingResumed);
    socket.on('party-state-update', handlePartyStateUpdate);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
      socket.off('market-bell', handleMarketBell);
      socket.off('trading-paused', handleTradingPaused);
      socket.off('trading-resumed', handleTradingResumed);
      socket.off('party-state-update', handlePartyStateUpdate);
    };
  }, []);

  // Pro mode specific state
  const [view, setView] = useState('grid'); // 'grid' or 'list' view for pro mode
  const [controlsExpanded, setControlsExpanded] = useState(false); // Mobile collapsible controls

  const [trading, setTrading] = useState(false);
  const [tradingStockId, setTradingStockId] = useState(null);
  
  // Check if trading is allowed based on market state
  const isTradingAllowed = () => {
    if (!marketState) return true; // Default to allow if no market state
    return marketState.isOpen && marketState.tradingEnabled;
  };

  // Safe formatter that handles NaN values
  const safeFormatCurrency = (value) => {
    return isNaN(value) ? '$0.00' : formatCurrency(value);
  };

  const safeFormatPercent = (value) => {
    return isNaN(value) ? '0.00%' : formatPercent(value);
  };

  // Removed diagnostic logging for production

  // Sort stocks based on selected order (same as original logic)
  // Code for sortedStocks would be here...
  const sortedStocks = React.useMemo(() => {
    if (!stocks || stocks.length === 0) return [];

    let sorted = [...stocks];

    switch (sortOrder) {
      case 'name-asc':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name-desc':
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'price-asc':
        sorted.sort((a, b) => (a.currentPrice || 0) - (b.currentPrice || 0));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.currentPrice || 0) - (a.currentPrice || 0));
        break;
      case 'change-asc':
        sorted.sort((a, b) => (a.change || 0) - (b.change || 0));
        break;
      case 'change-desc':
        sorted.sort((a, b) => (b.change || 0) - (a.change || 0));
        break;
      default:
        sorted.sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      sorted = sorted.filter(stock =>
        (stock.name?.toLowerCase() || '').includes(term) ||
        (stock.ticker?.toLowerCase() || '').includes(term)
      );
    }

    return sorted;
  }, [stocks, sortOrder, searchTerm]);

  useEffect(() => {
    // Setup market data subscription (replaces polling — socket broadcasts every 10s)
    const cleanupMarketDataSubscription = subscribeToMarketData(
      // Stock update handler
      (updates) => {
        setStocks(prevStocks => {
          // Build lookup map for O(1) access
          const updateMap = new Map(updates.map(u => [u.id, u]));

          return prevStocks.map(existingStock => {
            const update = updateMap.get(existingStock.id);
            if (!update) return existingStock;

            const newPrice = parseFloat(update.currentPrice);
            const newChange = parseFloat(update.change);

            // Append new price to history for MiniChart (keep last 100 points)
            let history = existingStock.history || [];
            const lastPoint = history[history.length - 1];
            if (!lastPoint || Math.abs(parseFloat(lastPoint.price) - newPrice) > 0.001) {
              history = [...history, { timestamp: new Date().toISOString(), price: newPrice }];
              if (history.length > 100) history = history.slice(-100);
            }

            return {
              ...existingStock,
              currentPrice: newPrice,
              change: newChange,
              history,
            };
          });
        });
      },
      // Market event handler
      (event) => {
        // Could show notification based on event
      }
    );

    return () => {
      cleanupMarketDataSubscription();
    };
  }, [userId]);

  // Polling removed — socket broadcasts every 10s are sufficient for live prices.
  // Initial data comes from fetchInitialStocks below.

  useEffect(() => {
    const handleConnect = () => {
      setSocketConnected(true);
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    // Socket connection listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Set initial connection state
    setSocketConnected(socket.connected);

    // Cleanup
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  // Real-time balance and position updates (debounced)
  const positionRefetchTimer = useRef(null);

  useEffect(() => {
    if (!userId) return;

    // Debounced position refetch — multiple events from one trade only trigger one fetch
    const debouncedFetchPositions = () => {
      if (positionRefetchTimer.current) clearTimeout(positionRefetchTimer.current);
      positionRefetchTimer.current = setTimeout(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/positions/${userId}`);
          if (response.ok) {
            const positionsData = await response.json();
            if (Array.isArray(positionsData)) {
              setPositions(positionsData.map(position => ({
                ...position,
                // currentValue/profit will be recalculated by the stocks useEffect below
              })));
            }
          }
        } catch (error) {
          console.error('Error fetching positions:', error);
        }
      }, 300);
    };

    const handleBalanceUpdate = (data) => {
      if (data.userId === userId && data.balance !== undefined) {
        setUserBalance(data.balance);
      }
    };

    const handlePositionUpdate = (data) => {
      if (data.userId === userId) {
        debouncedFetchPositions();
        if (data.balance !== undefined) {
          setUserBalance(data.balance);
        }
      }
    };

    const handleUserUpdate = (data) => {
      if (data.userId === userId) {
        if (data.balance !== undefined) setUserBalance(data.balance);
      }
    };

    socket.on('balance-update', handleBalanceUpdate);
    socket.on('position-update', handlePositionUpdate);
    socket.on('position-opened', handlePositionUpdate);
    socket.on('position-closed', handlePositionUpdate);
    socket.on('user-update', handleUserUpdate);
    socket.on('trade-executed', handlePositionUpdate);

    return () => {
      if (positionRefetchTimer.current) clearTimeout(positionRefetchTimer.current);
      socket.off('balance-update', handleBalanceUpdate);
      socket.off('position-update', handlePositionUpdate);
      socket.off('position-opened', handlePositionUpdate);
      socket.off('position-closed', handlePositionUpdate);
      socket.off('user-update', handleUserUpdate);
      socket.off('trade-executed', handlePositionUpdate);
    };
  }, [userId]);

  // Update position values when stock prices change — uses Map for O(1) lookups
  useEffect(() => {
    if (positions.length === 0 || stocks.length === 0) return;

    const stockMap = new Map(stocks.map(s => [s.id, s]));

    const updatedPositions = positions.map(position => {
      const stock = stockMap.get(position.stockId);
      if (!stock) return position;

      const stockCurrentPrice = stock.currentPrice || position.entryPrice || 0;
      let currentValue;

      if (position.type === 'LONG') {
        currentValue = position.shares * stockCurrentPrice;
      } else {
        const entryValue = position.shares * position.entryPrice;
        const priceDiff = position.entryPrice - stockCurrentPrice;
        currentValue = entryValue + (priceDiff * position.shares);
      }

      const profit = currentValue - position.initialValue;

      return {
        ...position,
        currentValue: parseFloat(currentValue.toFixed(2)),
        profit: parseFloat(profit.toFixed(2)),
        currentPrice: stockCurrentPrice,
        ticker: stock.ticker
      };
    });

    const hasChanges = updatedPositions.some((pos, i) =>
      pos.currentValue !== positions[i]?.currentValue ||
      pos.profit !== positions[i]?.profit
    );

    if (hasChanges) {
      setPositions(updatedPositions);
    }
  }, [stocks]);

  // Fetch initial data via API
  useEffect(() => {
    // Reset mount state reference
    componentMounted.current = true;
    setLoading(true);
    setError(null);

    const fetchInitialStocks = async () => {
      try {

        // Fetch stocks - using relative URL
        let stocksData = [];
        try {
          const response = await fetch(`${API_BASE_URL}/api/stocks`);
          if (response.ok) {
            stocksData = await response.json();
          } else {
            throw new Error(`HTTP error ${response.status}`);
          }
        } catch (e) {
          console.error('Failed to fetch stocks:', e);
          throw new Error('Failed to fetch stock data');
        }

        if (!componentMounted.current) return;

        // Process stocks data
        const validData = (stocksData || []).map(stock => {
          const currentPrice = parseFloat(stock.currentPrice) || parseFloat(stock.basePrice);
          const basePrice = parseFloat(stock.basePrice) || currentPrice;
          const change = basePrice > 0 ? ((currentPrice / basePrice) - 1) * 100 : 0;

          return {
            ...stock,
            currentPrice,
            basePrice,
            change: parseFloat(change.toFixed(2)),
          };
        });

        if (!componentMounted.current) return;
        setStocks(validData);

        // Don't auto-select - let user choose which stock to view
        // This keeps the chart hidden initially

        // Fetch user balance correctly (only if userId is valid)
        if (userId) {
          try {
            const userResponse = await fetch(`${API_BASE_URL}/api/validate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: userId,
                token: localStorage.getItem('token')
              })
            });

            if (userResponse.ok) {
              const userData = await userResponse.json();
              if (componentMounted.current) {
                setUserBalance(userData.balance || 10000);
              }
            } else {
              console.warn('Failed to fetch user balance:', userResponse.status);
              // Keep default balance
            }
          } catch (e) {
            console.error('Failed to fetch user balance:', e);
            // Keep default balance
          }
        }

        // Fetch positions (only if userId is valid)
        if (userId) {
          try {
            const positionsResponse = await fetch(`${API_BASE_URL}/api/positions/${userId}`);
          if (positionsResponse.ok) {
            const positionsData = await positionsResponse.json();
            if (componentMounted.current && Array.isArray(positionsData)) {
              // Process positions with current stock prices
              const stockMap = new Map(validData.map(s => [s.id, s]));
              const updatedPositions = positionsData.map(position => {
                const stock = stockMap.get(position.stockId);
                if (!stock) return position;

                const stockCurrentPrice = stock.currentPrice || position.entryPrice || 0;
                let currentValue;

                if (position.type === 'LONG') {
                  currentValue = position.shares * stockCurrentPrice;
                } else {
                  const entryValue = position.shares * position.entryPrice;
                  const priceDiff = position.entryPrice - stockCurrentPrice;
                  currentValue = entryValue + (priceDiff * position.shares);
                }

                const profit = currentValue - position.initialValue;

                return {
                  ...position,
                  currentValue: parseFloat(currentValue.toFixed(2)),
                  profit: parseFloat(profit.toFixed(2)),
                  currentPrice: stockCurrentPrice,
                  ticker: stock.ticker
                };
              });

              setPositions(updatedPositions);
            }
          } else {
            console.warn('Failed to fetch positions:', positionsResponse.status);
          }
          } catch (e) {
            console.error('Failed to fetch positions:', e);
          }
        }

      } catch (error) {
        console.error('Error in fetchInitialStocks:', error);
        if (componentMounted.current) {
          setError('Failed to load market data: ' + error.message);
        }
      } finally {
        if (componentMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchInitialStocks();

    return () => {
      componentMounted.current = false;
    };
  }, [userId]);

  // Terminal functions removed for mobile optimization

  // Handle trade execution (same as original function but with amount parameter)
  const handleTrade = async (stockId, type, amount = Math.max(1, parseInt(orderAmount) || 1)) => {
    // Check if trading is allowed
    if (!isTradingAllowed()) {
      const message = !marketState?.isOpen 
        ? 'Trading is closed. Wait for the opening bell!' 
        : 'Trading is temporarily paused.';
      showToast(message, 'error', 4000, null, 'TRADING RESTRICTED');
      return;
    }
    
    const stock = stocks.find(s => s.id === stockId);
    if (!stock) {
      showToast('Stock not found.', 'error', 3000, null, 'ERROR');
      return;
    }
    if (isNaN(stock.currentPrice) || stock.currentPrice <= 0) {
        showToast(`Invalid price for ${stock.ticker}. Cannot trade.`, 'error', 3000, null, 'INVALID PRICE');
        return;
    }

    const tradeValue = stock.currentPrice * amount;
    if (tradeValue > userBalance) {
        showToast(`Insufficient funds. Need ${safeFormatCurrency(tradeValue)}`, 'error', 4000, null, 'INSUFFICIENT FUNDS');
        return;
    }

    try {
      setTrading(true);
      setTradingStockId(stockId);
      const result = await executeOrder(userId, stockId, type, amount);

      // Show toast notification with clear title and details
      const title = `${type} POSITION`;
      const toastMessage = `${amount}x ${stock.ticker}`;
      const details = `Entry: ${safeFormatCurrency(result.position?.entryPrice || stock.currentPrice)} • Balance: ${safeFormatCurrency(result.balance)}`;
      showToast(toastMessage, type.toLowerCase(), 4000, details, title);

      // Terminal history removed

      if (result.balance !== undefined) setUserBalance(result.balance);
      if (result.positions) setPositions(result.positions);
    } catch (error) {
      showToast(`Trade failed: ${error.message || 'Unknown error'}`, 'error', 4000, null, 'TRADE FAILED');
    } finally {
      setTrading(false);
      setTradingStockId(null);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="market-loading">
        <div className="spinner"></div>
        <p>Loading market data...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="market-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  // Terminal removed for mobile optimization

  // Render pro mode stock list view
  const renderProListView = () => {
    if (!isProMode || view !== 'list') return null;

    return (
      <div className="pro-list-view">
        <table className="pro-stocks-table">
          <thead>
            <tr>
              <th>TICKER</th>
              <th>NAME</th>
              <th>PRICE</th>
              <th>CHG%</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {sortedStocks.map(stock => (
              <tr
                key={stock.id}
                onClick={() => navigate(`/stock/${stock.ticker}`)}
              >
                <td className="ticker-cell">{stock.ticker}</td>
                <td className="name-cell">{stock.name}</td>
                <td className="price-cell">{safeFormatCurrency(stock.currentPrice)}</td>
                <td className={`change-cell ${(stock.change || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {(stock.change || 0) >= 0 ? '+' : ''}
                  {safeFormatPercent(stock.change)}
                </td>
                <td className="actions-cell">
                  <button
                    className="pro-action-btn long"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrade(stock.id, 'LONG');
                    }}
                    disabled={trading || !isTradingAllowed()}
                    title={!isTradingAllowed() ? (!marketState?.isOpen ? 'Market is closed' : 'Trading is paused') : ''}
                  >
                    {trading && tradingStockId === stock.id ? '...' : 'LONG'}
                  </button>
                  <button
                    className="pro-action-btn short"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrade(stock.id, 'SHORT');
                    }}
                    disabled={trading || !isTradingAllowed()}
                    title={!isTradingAllowed() ? (!marketState?.isOpen ? 'Market is closed' : 'Trading is paused') : ''}
                  >
                    {trading && tradingStockId === stock.id ? '...' : 'SHORT'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Final render
  return (
    <div className={`market-container ${isProMode ? 'pro-market' : ''}`}>
      {/* Market Status Banner */}
      {marketState && !marketState.isOpen && (
        <div className="market-status-banner closed">
          <span className="banner-icon">🔴</span>
          <span className="banner-text">Market Closed</span>
        </div>
      )}
      {marketState && marketState.isOpen && !marketState.tradingEnabled && (
        <div className="market-status-banner paused">
          <span className="banner-icon">⏸️</span>
          <span className="banner-text">Trading Paused</span>
        </div>
      )}
      <div className="market-header">
        <button
          className="controls-toggle"
          onClick={() => setControlsExpanded(!controlsExpanded)}
        >
          {controlsExpanded ? '▲ HIDE CONTROLS' : '▼ SEARCH / SORT / VIEW'}
        </button>
        <div className={`market-controls ${controlsExpanded ? 'expanded' : ''}`}>
          <div className="search-container">
            <input
              type="text"
              placeholder="SEARCH..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="sort-container">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="sort-select"
            >
              <option value="default">Ticker</option>
              <option value="name-asc">A-Z</option>
              <option value="name-desc">Z-A</option>
              <option value="price-asc">Price ↑</option>
              <option value="price-desc">Price ↓</option>
              <option value="change-asc">Chg ↑</option>
              <option value="change-desc">Chg ↓</option>
            </select>
          </div>

          <div className="view-toggle">
            <button
              className={`view-btn ${view === 'grid' ? 'active' : ''}`}
              onClick={() => setView('grid')}
            >
              Grid
            </button>
            <button
              className={`view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >
              List
            </button>
          </div>
        </div>
      </div>

      <div className="user-status">
        <div className="balance">
          {isProMode ? 'BALANCE: ' : 'Balance: '}
          <FlickerText value={safeFormatCurrency(userBalance)} />
        </div>
        {/* {!socketConnected && (
          <div className="connection-status offline">
            {isProMode ? 'NO REALTIME FEED' : 'Real-time updates unavailable'}
          </div>
        )} */}
      </div>

      {/* Terminal removed for mobile optimization */}

      <div className="order-amount">
        <label htmlFor="order-amount">{'Shares per order:'}</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          id="order-amount"
          value={orderAmount}
          onChange={(e) => setOrderAmount(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={() => setOrderAmount(prev => String(Math.max(1, Math.min(100, parseInt(prev) || 1))))}
        />
      </div>

      {/* Pro Mode List View */}
      {renderProListView()}

      {/* Regular Grid or Pro Mode Grid */}
      {view === 'grid' && (
        <div className="stocks-grid">
          {sortedStocks.length > 0 ? (
            sortedStocks.map(stock => (
              <div
                key={stock.id}
                className="stock-card"
                onClick={() => navigate(`/stock/${stock.ticker}`)}
              >
                {/* Ticker - full width on its own line */}
                <h3 className="ticker">{stock.ticker || 'Unknown'}</h3>

                {/* Company name - full width on its own line */}
                <p className="name">{stock.name || 'Unknown Stock'}</p>

                {/* Price and change - on their own line */}
                <div className="stock-price-row">
                  <span className="price">
                    <FlickerText value={safeFormatCurrency(stock.currentPrice)} />
                  </span>
                  <span className={`change ${(stock.change || 0) >= 0 ? 'positive' : 'negative'}`}>
                    <FlickerText value={`${(stock.change || 0) >= 0 ? '+' : ''}${safeFormatPercent(stock.change)}`} />
                  </span>
                </div>

                {/* Mini chart for the stock */}
                <MiniChart history={stock.history} />

                <div className="trade-actions">
                  <button
                    className="trade-btn long"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrade(stock.id, 'LONG');
                    }}
                    disabled={trading || isNaN(stock.currentPrice) || stock.currentPrice <= 0 || !isTradingAllowed()}
                    title={!isTradingAllowed() ? (!marketState?.isOpen ? 'Market is closed' : 'Trading is paused') : ''}
                  >
                    {trading && tradingStockId === stock.id ? '...' : 'LONG'}
                  </button>
                  <button
                    className="trade-btn short"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrade(stock.id, 'SHORT');
                    }}
                    disabled={trading || isNaN(stock.currentPrice) || stock.currentPrice <= 0 || !isTradingAllowed()}
                    title={!isTradingAllowed() ? (!marketState?.isOpen ? 'Market is closed' : 'Trading is paused') : ''}
                  >
                    {trading && tradingStockId === stock.id ? '...' : 'SHORT'}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-stocks">
              <p>No stocks found matching your search. If this persists, try refreshing the page.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Market;